'use client';

import type { ReactNode } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { MotionReveal } from '@/components/MotionReveal';
import { useTranslations } from '@/hooks/useTranslations';

type AuthPageVariant = 'login' | 'register' | 'forgotPassword' | 'resetPassword' | 'verifyEmail';

type AuthPageShellProps = {
  variant: AuthPageVariant;
  children: ReactNode;
  footer?: ReactNode;
};

export function AuthPageShell({ variant, children, footer }: AuthPageShellProps) {
  const { t } = useTranslations();
  const baseKey = `authFlow.${variant}`;
  const renderedFooter = footer === undefined ? <DefaultAuthFooter variant={variant} /> : footer;

  return (
    <section className="relative isolate overflow-hidden px-4 py-10 sm:px-6 sm:py-14 lg:py-20">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_12%_18%,rgba(225,29,72,0.22),transparent_30%),radial-gradient(circle_at_88%_12%,rgba(37,99,235,0.18),transparent_32%),linear-gradient(180deg,rgba(15,19,36,0)_0%,rgba(15,19,36,0.76)_100%)]" />
      <MotionReveal
        immediate
        className="mx-auto grid w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-[#0f1324]/95 shadow-2xl shadow-black/30 lg:grid-cols-[0.95fr_1.05fr]"
      >
        <aside className="relative hidden min-h-[620px] overflow-hidden border-r border-white/10 bg-white/[0.03] p-10 lg:flex lg:flex-col">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,183,77,0.12),transparent_32%),radial-gradient(circle_at_55%_38%,rgba(251,113,133,0.2),transparent_28%)]" />
          <div className="relative z-10">
            <p className="inline-flex rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#ffd89a]">
              {t('authFlow.kicker')}
            </p>
            <h2 className="mt-6 max-w-sm text-4xl font-bold leading-tight tracking-tight text-white">
              {t('authFlow.trustTitle')}
            </h2>
            <p className="mt-4 max-w-md text-sm leading-6 text-slate-300">
              {t('authFlow.trustDescription')}
            </p>
          </div>

          <AuthIllustration variant={variant} />

          <div className="relative z-10 mt-auto grid gap-3">
            {['trustPoint1', 'trustPoint2', 'trustPoint3'].map((key) => (
              <div
                key={key}
                className="rounded-2xl border border-white/10 bg-[#15192b]/80 p-4 text-sm text-slate-200"
              >
                <span className="mr-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                  <CheckIcon />
                </span>
                {t(`authFlow.${key}`)}
              </div>
            ))}
          </div>
        </aside>

        <div className="p-6 sm:p-8 lg:p-12">
          <div className="lg:hidden">
            <p className="inline-flex rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#ffd89a]">
              {t('authFlow.kicker')}
            </p>
          </div>
          <div className="mx-auto w-full max-w-md">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-rose-200">
              {t(`${baseKey}.eyebrow`)}
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t(`${baseKey}.title`)}
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              {t(`${baseKey}.description`)}
            </p>

            <div className="mt-8 rounded-[1.5rem] border border-white/10 bg-[#15192b]/80 p-5 shadow-xl shadow-black/20 sm:p-6">
              {children}
            </div>

            {renderedFooter && <div className="mt-5 text-sm leading-6 text-slate-400">{renderedFooter}</div>}

            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-slate-300">
              <p className="font-semibold text-white">{t('authFlow.needHelpTitle')}</p>
              <p className="mt-1">
                {t('authFlow.needHelpText')}{' '}
                <LocalizedLink
                  href="/contact"
                  className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
                >
                  {t('authFlow.contactSupport')}
                </LocalizedLink>
              </p>
            </div>
          </div>
        </div>
      </MotionReveal>
    </section>
  );
}

function DefaultAuthFooter({ variant }: { variant: AuthPageVariant }) {
  const { t } = useTranslations();

  if (variant === 'login') {
    return (
      <div className="space-y-2">
        <p>
          <LocalizedLink
            className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
            href="/forgot-password"
          >
            {t('auth.forgotPassword')}
          </LocalizedLink>
        </p>
        <p>
          {t('authFlow.login.newReviewer')}{' '}
          <LocalizedLink
            className="font-semibold text-white underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
            href="/register"
          >
            {t('auth.createAccount')}
          </LocalizedLink>
        </p>
      </div>
    );
  }

  if (variant === 'forgotPassword' || variant === 'resetPassword') {
    return (
      <LocalizedLink
        className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
        href="/login"
      >
        {t('authFlow.backToSignIn')}
      </LocalizedLink>
    );
  }

  if (variant === 'verifyEmail') {
    return (
      <p>
        {t('authFlow.verifyEmail.alreadyVerified')}{' '}
        <LocalizedLink
          className="font-semibold text-white underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
          href="/login"
        >
          {t('auth.signIn')}
        </LocalizedLink>
      </p>
    );
  }

  return null;
}

function AuthIllustration({ variant }: { variant: AuthPageVariant }) {
  const isRecovery = variant === 'forgotPassword' || variant === 'resetPassword';
  const isVerify = variant === 'verifyEmail';
  const accent = isRecovery ? '#2563eb' : isVerify ? '#10b981' : '#e11d48';

  return (
    <div className="relative z-10 my-10 flex justify-center" aria-hidden>
      <svg viewBox="0 0 360 280" className="h-72 w-full max-w-md" fill="none">
        <defs>
          <linearGradient id="auth-card-gradient" x1="78" y1="34" x2="286" y2="238" gradientUnits="userSpaceOnUse">
            <stop stopColor="#fff7ed" />
            <stop offset="1" stopColor="#fecdd3" />
          </linearGradient>
          <linearGradient id="auth-chip-gradient" x1="120" y1="78" x2="238" y2="200" gradientUnits="userSpaceOnUse">
            <stop stopColor="#ffb74d" />
            <stop offset="1" stopColor={accent} />
          </linearGradient>
          <filter id="auth-illustration-shadow" x="36" y="18" width="292" height="236" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feDropShadow dx="0" dy="22" stdDeviation="18" floodColor="#000000" floodOpacity="0.3" />
          </filter>
        </defs>
        <ellipse cx="178" cy="237" rx="116" ry="22" fill="#020617" opacity="0.34" />
        <path d="M42 86C77 52 112 55 147 91C185 129 218 129 255 91C288 57 322 58 342 83" stroke="#475569" strokeWidth="3" strokeLinecap="round" strokeDasharray="7 13" opacity="0.45" />
        <circle cx="54" cy="61" r="9" fill="#fb7185" opacity="0.72" />
        <circle cx="309" cy="62" r="12" fill="#ffb74d" opacity="0.7" />

        <g filter="url(#auth-illustration-shadow)">
          <rect x="82" y="48" width="196" height="168" rx="28" fill="url(#auth-card-gradient)" />
          <rect x="105" y="78" width="84" height="10" rx="5" fill="#881337" opacity="0.18" />
          <rect x="105" y="102" width="150" height="12" rx="6" fill="#881337" opacity="0.13" />
          <rect x="105" y="127" width="112" height="12" rx="6" fill="#881337" opacity="0.13" />
          <rect x="105" y="164" width="150" height="28" rx="14" fill="#111827" opacity="0.9" />
          <circle cx="244" cy="85" r="21" fill="#111827" opacity="0.92" />
          {isRecovery ? (
            <path d="M237 86H251M244 79V93" stroke="#bfdbfe" strokeWidth="4" strokeLinecap="round" />
          ) : isVerify ? (
            <path d="M235 85L242 92L254 78" stroke="#bbf7d0" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M237 86C241 80 247 80 251 86" stroke="#fecdd3" strokeWidth="4" strokeLinecap="round" />
          )}
        </g>

        <g transform="translate(131 103)">
          <circle cx="50" cy="50" r="47" fill="url(#auth-chip-gradient)" stroke="#fff7ed" strokeWidth="7" />
          <circle cx="50" cy="50" r="32" fill="#111827" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <path d="M50 10V24M50 76V90M10 50H24M76 50H90" stroke="#fff7ed" strokeWidth="6" strokeLinecap="round" />
          <path d="M36 51L46 61L66 39" stroke="#fff7ed" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </svg>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <path d="M3.25 8.25L6.25 11.25L12.75 4.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
